// 底部 5 tab 导航（手机模式）—— APAC / Mexico IA
// - 一级入口：Monitoreo / Incidencias / Analitica / Operacion / Cuenta
// - 选中态：橙色填充圆 + 文字橙色（圆形圆角填充背景）
// - 顶部 1px 分割线
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
	AppstoreOutlined,
	VideoCameraOutlined,
	FileTextOutlined,
	HomeOutlined,
	ThunderboltOutlined,
	MessageOutlined,
	UserOutlined,
	ShopOutlined,
} from '@ant-design/icons';
import type { ComponentType, ReactNode } from 'react';
import { useRegion } from '../hooks/useRegion.js';
import { mobileFontSize, mobileFontWeight } from '../theme/mobile-typography.js';

const PRIMARY = '#FF7043';
const TAB_BAR_HEIGHT = 64;
const TAB_BAR_BUILD_MARK = 'apac-store-nav-2026-05-14';

interface TabDef {
	key: string;
	to: string;
	i18nKey: string;
	Icon: ComponentType<{ className?: string; style?: React.CSSProperties }>;
	activePrefixes: string[];
	badge?: ReactNode;
}

const TABS: TabDef[] = [
	{ key: 'monitor',    to: '/m/monitor',    i18nKey: 'tabbar.monitor',    Icon: VideoCameraOutlined, activePrefixes: ['/m/monitor', '/m/video', '/m/player', '/m/multi-view', '/m/multi-select'] },
	{ key: 'incidents',  to: '/m/incidents',  i18nKey: 'tabbar.incidents',  Icon: MessageOutlined,     activePrefixes: ['/m/incidents', '/m/messages'] },
	{ key: 'analytics',  to: '/m/analytics',  i18nKey: 'tabbar.analytics',  Icon: FileTextOutlined,    activePrefixes: ['/m/analytics', '/m/reports', '/cloudstorage'] },
	{ key: 'operations', to: '/m/operations', i18nKey: 'tabbar.operations', Icon: ThunderboltOutlined, activePrefixes: ['/m/operations', '/m/workbench'] },
	{ key: 'account',    to: '/m/account',    i18nKey: 'tabbar.account',    Icon: UserOutlined,        activePrefixes: ['/m/account', '/m/profile', '/m/recent', '/m/album', '/m/repair', '/m/hotline', '/m/feedback', '/m/changelog', '/m/settings'] },
];

const APAC_TABS: TabDef[] = [
	{ key: 'home',        to: '/m/home',        i18nKey: 'tabbar.home',        Icon: HomeOutlined,     activePrefixes: ['/m/home'] },
	{ key: 'monitor',     to: '/m/monitor',     i18nKey: 'tabbar.store',       Icon: ShopOutlined,     activePrefixes: ['/m/monitor', '/m/video', '/m/player', '/m/multi-view', '/m/multi-select'] },
	{ key: 'application', to: '/m/application', i18nKey: 'tabbar.application', Icon: AppstoreOutlined, activePrefixes: ['/m/application', '/m/snapshot-inspection', '/m/incidents', '/m/messages', '/m/analytics', '/m/reports', '/m/operations', '/m/workbench', '/event', '/message', '/alert', '/customer', '/member', '/traffic', '/cloudstorage', '/patrol', '/aipatrol', '/scan', '/sharedevice', '/pos', '/smart-listening', '/phonelive', '/evaluation', '/inspect', '/wechatvip', '/university', '/datawarn', '/heatmap', '/leavepost', '/rewardpunish', '/staff'] },
	{ key: 'account',     to: '/m/account',     i18nKey: 'tabbar.profile',     Icon: UserOutlined,     activePrefixes: ['/m/account', '/m/profile', '/m/recent', '/m/album', '/m/repair', '/m/hotline', '/m/feedback', '/m/changelog', '/m/settings', '/m/repair-list'] },
];

export const BOTTOM_TAB_BAR_HEIGHT = TAB_BAR_HEIGHT;

export default function BottomTabBar() {
	const { t } = useTranslation();
	const location = useLocation();
	const region = useRegion();
	const tabs = region === 'apac' ? APAC_TABS : TABS;
	const isApac = region === 'apac';
	return (
		<nav
			className={isApac ? 'ulucu-bottom-tabbar ulucu-bottom-tabbar--apac' : 'ulucu-bottom-tabbar'}
			data-build={TAB_BAR_BUILD_MARK}
			style={{
				position: 'fixed',
				left: 0,
				right: 0,
				bottom: 0,
				height: `calc(${TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom))`,
				boxSizing: 'border-box',
				background: '#fff',
				borderTop: `1px solid ${isApac ? '#eef0f3' : '#f0f0f0'}`,
				display: 'flex',
				justifyContent: 'space-around',
				alignItems: 'stretch',
				zIndex: 1200,
				paddingBottom: 'env(safe-area-inset-bottom)',
			}}
		>
			{tabs.map(({ key, to, i18nKey, Icon, activePrefixes, badge }) => {
				const isActive = activePrefixes.some((prefix) => location.pathname.startsWith(prefix));
				const label = t(i18nKey);
				return (
				<Link
					key={key}
					to={to}
					aria-current={isActive ? 'page' : undefined}
					style={{
						flex: 1,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						gap: isApac ? 1 : 2,
						color: isActive ? PRIMARY : '#666',
						textDecoration: 'none',
						fontSize: mobileFontSize.caption,
						position: 'relative',
					}}
				>
					<div
						style={{
							width: 40,
							height: 28,
							borderRadius: 14,
							background: isActive ? '#fff0e8' : 'transparent',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							transition: 'background 0.15s',
							position: 'relative',
						}}
					>
						<Icon className="ulucu-mobile-icon" style={{ fontSize: isApac ? 19 : 20, color: isActive ? PRIMARY : '#444' }} />
						{badge && (
							<span
								style={{
									position: 'absolute',
									top: -4,
									right: 0,
									background: '#FF4D4F',
									color: '#fff',
									fontSize: 9,
									borderRadius: 8,
									padding: '0 5px',
									lineHeight: '14px',
									fontWeight: mobileFontWeight.medium,
								}}
							>
								{badge}
							</span>
						)}
					</div>
					<span
						style={{
							maxWidth: '100%',
							padding: '0 2px',
							fontWeight: isActive ? mobileFontWeight.medium : mobileFontWeight.regular,
							lineHeight: 1.15,
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
						}}
					>
						{label}
					</span>
				</Link>
				);
			})}
		</nav>
	);
}
